/**
 * "Publish as an example" — put a finished run on the public gallery.
 *
 * Offered only to platform admins, because this is a marketing action rather than a customer
 * feature: it decides what a stranger sees on the homepage. The endpoint checks for itself, so this
 * is an offer and not a gate — the same arrangement as the admin link in the rail.
 *
 * Publishing copies the rendered page out rather than sharing the live one, so what goes public is
 * a recording. That is worth saying on the dialog: somebody who expects the public page to follow
 * later re-runs would otherwise be surprised by the one thing they cannot see from here.
 */

import { Globe } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { usePublishExample } from "@/lib/queries";
import { useBranding } from "@/lib/useBranding";

/** From a title, the address it will live at: "GBI pilot · London" -> "gbi-pilot-london". */
function slugify(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

export function PublishExample({
  studyId,
  runId,
  suggestedTitle,
  suggestedFigure,
}: {
  studyId: string;
  runId: string;
  suggestedTitle: string;
  /** The headline number from this run, so the gallery card has something true on it already. */
  suggestedFigure: string;
}) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(suggestedTitle);
  const [slug, setSlug] = useState(slugify(suggestedTitle));
  const [summary, setSummary] = useState("");
  const [figure, setFigure] = useState(suggestedFigure);
  const [touchedSlug, setTouchedSlug] = useState(false);
  const publish = usePublishExample(studyId, runId);
  const { brand } = useBranding();

  function retitle(next: string) {
    setTitle(next);
    if (!touchedSlug) setSlug(slugify(next));
  }

  async function go() {
    try {
      await publish.mutateAsync({ slug, title, summary, headline_figure: figure, position: 0 });
      toast.success(`Published at /examples/${slug}`);
      setOpen(false);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "could not publish");
    }
  }

  return (
    <>
      <Button variant="ghost" size="sm" onClick={() => setOpen(true)}>
        <Globe aria-hidden="true" /> Publish as an example
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Publish as a public example</DialogTitle>
            <DialogDescription>
              Anyone will be able to read this run without signing in. It is copied as it stands —
              re-running the study later changes nothing here until you publish again.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label htmlFor="ex-title">Title</Label>
              <Input id="ex-title" value={title} onChange={(e) => retitle(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="ex-slug">Address</Label>
              <Input
                id="ex-slug"
                value={slug}
                onChange={(e) => {
                  setTouchedSlug(true);
                  setSlug(e.target.value);
                }}
              />
              <p className="mt-1 text-xs text-muted-foreground">{brand.primary_domain}/examples/{slug || "…"}</p>
            </div>
            <div>
              <Label htmlFor="ex-summary">One line for the gallery</Label>
              <Input
                id="ex-summary"
                value={summary}
                placeholder="A city consultation message, put to 30 simulated residents."
                onChange={(e) => setSummary(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="ex-figure">The figure worth reading first</Label>
              <Input id="ex-figure" value={figure} onChange={(e) => setFigure(e.target.value)} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={publish.isPending || summary.trim().length < 10 || !slug}
              onClick={() => void go()}
            >
              {publish.isPending ? "Publishing…" : "Publish"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
